<template>
  <div class="row">
    <select-component
      modelName="country"
      :rowClass="`${showZipCode ? 'col-3' : 'col-4'}`"
      valueOption="id"
      :dataOptions="countries"
      :isMultiple="false"
      :initialSelection="model.country"
      :requireClass="`${requiredFields ? 'required-' : ''}country`"
      :labelSelect="`${$t('common_words.country')}: ${
        requiredFields ? '*' : ''
      }`"
      @onChange="handleChangeSelected"
      @onClear="
        model.country = null;
        model.state = null;
        model.city = null;
      "
    />
    <select-component
      modelName="state"
      :rowClass="`${showZipCode ? 'col-3' : 'col-4'}`"
      valueOption="id"
      :dataOptions="states"
      :isMultiple="false"
      :initialSelection="model.state"
      :labelSelect="`${$t('common_words.state')}: ${requiredFields ? '*' : ''}`"
      :requireClass="`${requiredFields ? 'required-' : ''}state`"
      @onChange="handleChangeSelected"
      @onClear="
        model.state = null;
        model.city = null;
      "
    />
    <select-component
      modelName="city"
      :rowClass="`${showZipCode ? 'col-3' : 'col-4'}`"
      valueOption="id"
      :dataOptions="cities"
      :isMultiple="false"
      :initialSelection="model.city"
      :labelSelect="`${$t('common_words.city')}: ${requiredFields ? '*' : ''}`"
      :requireClass="`${requiredFields ? 'required-' : ''}city`"
      @onChange="handleChangeSelected"
      @onClear="model.city = null"
    />
    <div v-if="showZipCode" :class="`${showZipCode ? 'col-3' : 'col-4'}`">
      <fg-input
        type="text"
        placeholder="470001"
        name="common_words.zip_code"
        v-model="model.zip_code"
        v-validate="'alpha_num|max:35'"
        :label="`${$t('common_words.zip_code')}: `"
        :error="getError('common_words.zip_code', true)"
        @change="handleChange('zip_code')"
      >
      </fg-input>
    </div>
    <div v-if="showAddress" class="col-12">
      <div class="form-group has-label">
        <label>{{ $t('common_words.address') }}</label>
        <br />
        <el-input
          maxlength="200"
          show-word-limit
          class="shadow-sm"
          type="textarea"
          placeholder="Carrera XX, vía XXXX, XXXX - Colombia"
          v-model="model.address"
          :rows="2"
          @change="handleChange('address')"
        ></el-input>
        <br />
      </div>
    </div>
  </div>
</template>

<script>
import { Axios } from 'src/class/Axios.js';
import { isset } from 'src/util/Util.js';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinValidations from 'src/mixins/forms/mixin-validations';
import SweetAlert from 'src/class/Alerts';

export default {
  name: 'select-address',
  data() {
    return {
      model: {
        address: null,
        zip_code: null,
        country: null,
        state: null,
        city: null,
      },
      countries: [],
      states: [],
      cities: [],
    };
  },
  props: {
    showAddress: {
      type: Boolean,
      default: true,
      description: 'Shows the Address textarea',
    },
    showZipCode: {
      type: Boolean,
      default: true,
      description: 'Shows the zip_code input',
    },
    requiredFields: {
      type: Boolean,
      default: false,
      description: 'Required only the selects inputs',
    },
    usingIds: {
      type: Boolean,
      default: false,
      description: 'Using Ids with FK in DB instead of value',
    },
  },
  mixins: [MixinForm, MixinValidations],
  methods: {
    /**
     * Encuentra la mejor coincidencia en la lista ignorando acentos y mayúsculas/minúsculas.
     */
    findClosestMatch(value, options) {
      if (isset(value) && isset(options)) {
        const normalizeText = (text) =>
          text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, ''); // Elimina acentos

        const normalizedValue = normalizeText(value);

        return (
          options.find(
            (option) => normalizeText(option.name) === normalizedValue
          ) || null
        );
      } else {
        return null;
      }
    },

    async fetchCountries() {
      this.$emit('showHideLoading', true);
      try {
        const response = await Axios.getMasters('countries');
        this.countries = response.data.data;
      } catch (error) {
        SweetAlert.warning(error.response.data.message);
      } finally {
        this.$emit('showHideLoading', false);
      }
    },

    async fetchStates() {
      if (!this.model.country) return;
      this.$emit('showHideLoading', true);
      this.model.state = null;
      this.model.city = null;

      try {
        const response = await Axios.postMasters({
          route: 'regions',
          country_id: this.model.country,
        });
        this.states = response.data.data;
      } catch (error) {
        SweetAlert.warning(error.response.data.message);
      } finally {
        this.$emit('showHideLoading', false);
      }
    },

    async fetchCities() {
      if (!this.model.country || !this.model.state) return;
      this.$emit('showHideLoading', true);
      this.model.city = null;

      try {
        const response = await Axios.postMasters({
          route: 'cities',
          country_id: this.model.country,
          region_id: this.model.state,
        });
        this.cities = response.data.data;
      } catch (error) {
        SweetAlert.warning(error.response.data.message);
      } finally {
        this.$emit('showHideLoading', false);
      }
    },

    handleChangeSelected(data) {
      this.$set(this.model, data.name, data.selects);

      switch (data.name) {
        case 'country':
          this.fetchStates();
          this.cities = [];
          this.handleChange('country');

          break;

        case 'state':
          this.fetchCities();
          this.handleChange('state');
          break;

        case 'city':
          this.handleChange('city');
          break;

        default:
          break;
      }
    },

    handleChange(name) {
      let selectedData = this.model[name];

      const mapFields = ['country', 'state', 'city'];

      if (!this.usingIds && selectedData && mapFields.includes(name)) {
        const lookup = {
          country: this.countries,
          state: this.states,
          city: this.cities,
        };

        const foundItem = lookup[name].find((item) => item.id === selectedData);
        selectedData = foundItem ? foundItem.name : '';
      }

      this.$emit('onChange', { name, selects: selectedData });
    },

    validate() {
      if (!isset(this.model.country)) {
        this.validationInputsRequired('country');

        return false;
      } else if (!isset(this.model.state)) {
        this.validationInputsRequired('state');

        return false;
      } else if (!isset(this.model.city)) {
        this.validationInputsRequired('city');

        return false;
      } else {
        return true;
      }
    },
  },
  async created() {
    await this.fetchCountries();

    if (!sessionStorage.getItem('editForm')) {
      this.model.country = null;
    } else {
      const { address, country, state, city, zip_code } = JSON.parse(
        sessionStorage.getItem('editForm')
      );

      if (this.usingIds) {
        this.model = {
          ...this.model,
          country,
          state,
          city,
          address,
          zip_code,
        };
        this.handleChange('country');
        this.handleChange('state');
        this.handleChange('city');
        this.handleChange('address');
        this.handleChange('zip_code');
      } else {
        this.model = {
          ...this.model,
          address,
          zip_code,
        };
        this.handleChange('address');
        this.handleChange('zip_code');

        const matchedCountry = this.findClosestMatch(country, this.countries);
        if (matchedCountry) {
          this.model.country = matchedCountry.id;
          this.handleChange('country');

          await this.fetchStates();

          const matchedState = this.findClosestMatch(state, this.states);
          if (matchedState) {
            this.model.state = matchedState.id;
            this.handleChange('state');

            await this.fetchCities();

            const matchedCity = this.findClosestMatch(city, this.cities);
            if (matchedCity) {
              this.model.city = matchedCity.id;
              this.handleChange('city');
            }
          }
        }
      }
    }
  },
  mounted() {
    this.$on('validateAddress', (callback) => {
      const result = this.validate();
      if (callback) {
        callback(result);
      }
    });
  },
};
</script>
